import { Message } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface WebSearchResult {
  title: string;
  url: string;
  snippet: string;
  publishedDate?: string;
  source?: string;
}

export interface ResearchPaper {
  id: string;
  doi: string;
  summary: string;
  embedding?: number[]; // Optional since frontend doesn't need it
  similarity_score?: number;
}

export interface ChatRequest {
  message: string;
  conversationId?: string;
  userId?: string;
  useWebSearch?: boolean;
  context?: {
    petInfo?: {
      breed?: string;
      age?: number;
      weight?: number;
      activityLevel?: 'low' | 'moderate' | 'high';
      healthConditions?: string[];
      currentFood?: string;
    };
    requestType?: 'nutrition_advice' | 'recipe_development' | 'general_question';
  };
}

export interface ChatResponse {
  message: string;
  conversationId: string;
  messageId: string;
  timestamp: string;
  webSearchResults?: WebSearchResult[];
  metadata?: {
    tokensUsed?: number;
    model?: string;
    responseTime?: number;
    webSearchUsed?: boolean;
    searchQuery?: string;
  };
}

export interface ConversationHistory {
  conversation: {
    id: string;
    createdAt: string;
    updatedAt: string;
    userId?: string;
  };
  messages: Message[];
  stats: {
    messageCount: number;
    userMessages: number;
    assistantMessages: number;
    duration: number;
    lastActivity: string;
  };
}

export interface APIError {
  error: string;
  message: string;
  statusCode: number;
  timestamp: string;
  conversationId?: string;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData: APIError = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error('Network error occurred. Please check your connection.');
    }
  }

  /**
   * Send a chat message and get AI response
   */
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    return this.request<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Store the current abort controller for cancellation
  private currentAbortController: AbortController | null = null;

  /**
   * Cancel any ongoing streaming request
   */
  cancelStream(): void {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
  }

  /**
   * Send a chat message and get streaming AI response
   */
  async sendStreamingMessage(
    request: ChatRequest,
    onChunk: (content: string) => void,
    onComplete: (messageId: string, metadata?: any, webSearchResults?: WebSearchResult[], researchPapers?: ResearchPaper[], classification?: any) => void,
    onError: (error: string) => void,
    onSearchStatus?: (status: string) => void
  ): Promise<string> {
    // Cancel any previous ongoing request
    this.cancelStream();
    
    // Create new abort controller for this request
    this.currentAbortController = new AbortController();
    const { signal } = this.currentAbortController;
    
    const url = `${API_BASE_URL}/chat/stream`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal, // Pass abort signal to fetch
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let conversationId = '';
      let messageId = '';
      let buffer = ''; // Buffer to handle incomplete JSON across chunks

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete lines from buffer
        const lines = buffer.split('\n');
        
        // Keep the last potentially incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() && line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6).trim();
              if (jsonStr) {
                const data = JSON.parse(jsonStr);
                
                switch (data.type) {
                  case 'start':
                    conversationId = data.conversationId;
                    messageId = data.messageId;
                    break;
                    
                  case 'content':
                    onChunk(data.content);
                    break;
                    
                  case 'search_status':
                    if (onSearchStatus) {
                      onSearchStatus(data.status);
                    }
                    break;
                    
                  case 'done':
                    onComplete(data.messageId, data.metadata, data.webSearchResults, data.researchPapers, data.classification);
                    return conversationId;
                    
                  case 'error':
                    onError(data.error);
                    return conversationId;
                }
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line.substring(0, 100) + '...');
              console.error('Parse error details:', parseError);
            }
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim() && buffer.startsWith('data: ')) {
        try {
          const jsonStr = buffer.slice(6).trim();
          if (jsonStr) {
            const data = JSON.parse(jsonStr);
            if (data.type === 'done') {
              onComplete(data.messageId, data.metadata, data.webSearchResults, data.researchPapers, data.classification);
            }
          }
        } catch (parseError) {
          console.warn('Failed to parse final SSE data:', buffer.substring(0, 100) + '...');
        }
      }

      // Clean up abort controller on successful completion
      this.currentAbortController = null;
      return conversationId;

    } catch (error) {
      // Clean up abort controller
      this.currentAbortController = null;
      
      // Don't treat abort as an error - it's intentional cancellation
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Stream cancelled by user');
        return '';
      }
      
      console.error('Streaming request failed:', error);
      onError(error instanceof Error ? error.message : 'Streaming failed');
      throw error;
    }
  }

  /**
   * Get conversation history
   */
  async getConversationHistory(
    conversationId: string,
    limit?: number
  ): Promise<ConversationHistory> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<ConversationHistory>(`/conversation/${conversationId}${params}`);
  }

  /**
   * Get user conversations
   */
  async getConversations(params?: {
    userId?: string;
    search?: string;
    limit?: number;
  }): Promise<{
    conversations: Array<{
      id: string;
      createdAt: string;
      updatedAt: string;
      messageCount: number;
      lastMessage: string;
    }>;
    total: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params?.userId) searchParams.append('userId', params.userId);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const endpoint = `/conversations${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<{
    success: boolean;
    message: string;
    conversationId: string;
  }> {
    return this.request(`/conversation/${conversationId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Check API health
   */
  async checkHealth(): Promise<{
    status: string;
    timestamp: string;
    services: {
      openai: string;
      conversations: string;
    };
    stats: {
      totalConversations: number;
      totalMessages: number;
      activeConversations: number;
      averageMessagesPerConversation: number;
    };
  }> {
    return this.request('/chat/health');
  }

  /**
   * Determine request type based on message content
   */
  determineRequestType(message: string): 'nutrition_advice' | 'recipe_development' | 'general_question' {
    const lowerMessage = message.toLowerCase();
    
    if (
      lowerMessage.includes('recipe') ||
      lowerMessage.includes('formula') ||
      lowerMessage.includes('ingredient') ||
      lowerMessage.includes('develop') ||
      lowerMessage.includes('create food') ||
      lowerMessage.includes('make food')
    ) {
      return 'recipe_development';
    }
    
    if (
      lowerMessage.includes('nutrition') ||
      lowerMessage.includes('diet') ||
      lowerMessage.includes('feeding') ||
      lowerMessage.includes('food recommendation') ||
      lowerMessage.includes('what should') ||
      lowerMessage.includes('best food')
    ) {
      return 'nutrition_advice';
    }
    
    return 'general_question';
  }

  /**
   * Extract pet info from navigation context
   */
  createPetContext(navItem?: string): ChatRequest['context'] {
    const requestType = navItem ? this.determineRequestType(navItem) : 'general_question';
    
    return {
      requestType,
      // You can extend this to collect pet info from user profile/forms
      petInfo: {
        // These could be populated from user settings/profile
        // breed: userProfile?.petBreed,
        // age: userProfile?.petAge,
        // weight: userProfile?.petWeight,
        // activityLevel: userProfile?.petActivityLevel,
      }
    };
  }
}

export const apiService = new ApiService();
export default apiService;
